import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import type { ScenarioStateResponse } from "@/api/types";
import { projectFrontierTrajectory } from "@/utils/frontierEndingProjection";

/**
 * 엔딩 궤적 나침반 — 현재 상태에서 가장 가까운 엔딩 방향을 1줄 태그로 표시.
 *
 * <p>사용자 결정 (2026-04-22 A.2 = B): 확률 % 는 노출하지 않는다. 궤적 태그만.
 * 플레이어가 "내가 어디로 가고 있는지" 를 인지하되, 메타게임 (확률 극대화) 유도
 * 방지.
 *
 * <p>현재 frontier-vanguard 전용. 다른 시나리오 팩이 추가되면 프로젝터 함수 확장.
 */
interface EndingCompassProps {
    scenarioId: string;
    scenarioState: ScenarioStateResponse | null | undefined;
    /** 축약 모드 — HUD 좁은 칸에 넣을 때. 기본 false (detail 본문까지 렌더). */
    compact?: boolean;
    recentFootprints?: string[];
}

export function EndingCompass({ scenarioId, scenarioState, compact = false, recentFootprints = [] }: EndingCompassProps) {
    if (scenarioId !== "frontier-vanguard") return null;

    const trajectory = projectFrontierTrajectory(scenarioState);
    const toneColor = resolveToneColor(trajectory.tone);
    const traces = buildTrajectoryTraces(scenarioState, trajectory.id, compact ? 1 : 2);

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: tokens.easing.decelerate }}
            className="rounded-m px-m py-s relative overflow-hidden"
            style={{
                background: `radial-gradient(circle at 0% 0%, ${toneColor}12 0%, transparent 55%), linear-gradient(180deg, ${tokens.color.ink}E8, ${tokens.color.ink}C4)`,
                border: `1px solid ${toneColor}66`,
                boxShadow: `0 10px 24px ${tokens.color.ink}77, 0 0 18px ${toneColor}22, inset 0 1px 0 ${toneColor}33`,
            }}
            role="status"
            aria-label={`현재 엔딩 궤적: ${trajectory.label}`}
        >
            {/* 2026-04-23: 좌측 tone accent — Spirytale 언어 통일. */}
            <div
                aria-hidden
                className="absolute inset-y-0 left-0"
                style={{
                    width: 2,
                    background: `linear-gradient(180deg, ${toneColor}FF 0%, ${toneColor}44 100%)`,
                    boxShadow: `0 0 8px ${toneColor}AA`,
                }}
            />
            <div className="flex items-center gap-s pl-[6px]">
                <motion.div
                    aria-hidden
                    className="rounded-full flex items-center justify-center shrink-0"
                    animate={{
                        textShadow: [
                            `0 0 10px ${toneColor}AA`,
                            `0 0 18px ${toneColor}FF, 0 0 4px #FFF6D888`,
                            `0 0 10px ${toneColor}AA`,
                        ],
                    }}
                    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        width: 26,
                        height: 26,
                        background: `radial-gradient(circle, ${toneColor}44, ${toneColor}14)`,
                        border: `1px solid ${toneColor}99`,
                        color: toneColor,
                        fontSize: 15,
                        boxShadow: `inset 0 1px 0 ${toneColor}44`,
                    }}
                >
                    ✦
                </motion.div>
                <div className="flex-1 min-w-0">
                    {/* 2026-04-25: "길잡이 · 지금의 궤적" kicker 제거 — trajectory.label 이 스스로 설명. */}
                    <div
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            letterSpacing: "0.04em",
                            textShadow: `0 0 14px ${toneColor}66, 0 1px 0 ${tokens.color.ink}`,
                        }}
                    >
                        {trajectory.label}
                    </div>
                </div>
            </div>
            {!compact && (
                <div
                    className="mt-xs pl-[6px] font-body"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 13.5,
                        lineHeight: tokens.type.lineHeight.normal,
                        opacity: 0.9,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {trajectory.detail}
                </div>
            )}
            {traces.length > 0 && (
                <div className="mt-xs pl-[6px]">
                    <div
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 13.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            opacity: 0.9,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        왜 지금 이 궤적인가
                    </div>
                    <div className="mt-[6px] flex flex-wrap gap-[6px]">
                        {traces.map((trace) => (
                            <span
                                key={trace}
                                className="rounded-full px-xs py-[4px] font-body"
                                style={{
                                    background: `${toneColor}14`,
                                    border: `1px solid ${toneColor}33`,
                                    color: tokens.color.parchment,
                                    fontSize: 13,
                                    lineHeight: 1.35,
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                            >
                                {trace}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {recentFootprints.length > 0 && (
                <div className="mt-xs pl-[6px]">
                    <div
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 13.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            opacity: 0.9,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        최근 선택 흔적
                    </div>
                    <div className="mt-[6px] flex flex-wrap gap-[6px]">
                        {recentFootprints.slice(0, compact ? 1 : 2).map((trace) => (
                            <span
                                key={trace}
                                className="rounded-full px-xs py-[4px] font-body"
                                style={{
                                    background: `${tokens.color.parchment}12`,
                                    border: `1px solid ${tokens.color.parchment}2A`,
                                    color: tokens.color.parchment,
                                    fontSize: 13,
                                    lineHeight: 1.35,
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                            >
                                {trace}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </motion.div>
    );
}

function buildTrajectoryTraces(
    scenarioState: ScenarioStateResponse | null | undefined,
    trajectoryId: string,
    maxItems: number,
): string[] {
    if (!scenarioState) return [];
    const tags = scenarioState.outcomeTags ?? [];
    const traces: string[] = [];
    const hasTag = (value: string) => tags.includes(value);
    const push = (value: string) => {
        if (!traces.includes(value)) traces.push(value);
    };

    if (trajectoryId === "conquest") {
        if (hasTag("conquest")) push("최근 정복 쪽 결단이 누적되었습니다");
        if (scenarioState.troopMorale >= 10) push("전열이 더 앞으로 밀어붙일 준비를 하고 있습니다");
        if (scenarioState.frontierTrust <= -10) push("변경의 마음이 닫히며 강경한 결로 기웁니다");
    }

    if (trajectoryId === "armistice") {
        if (hasTag("brokered-peace")) push("화의와 약조의 흔적이 남아 있습니다");
        if (hasTag("bound-company")) push("병사와 주민을 함께 묶으려는 선택이 쌓였습니다");
        if (scenarioState.supplyLineState === "stable" || scenarioState.supplyLineState === "delayed") {
            push("보급선이 아직 협상의 시간을 버텨 주고 있습니다");
        }
    }

    if (trajectoryId === "guardian-return") {
        if (hasTag("living-return")) push("살아 돌아오는 선을 먼저 붙들었습니다");
        if (hasTag("protector")) push("끝까지 지키려는 선택이 누적되었습니다");
        if (scenarioState.memorialWeight >= 3) push("잃은 이름을 외면하지 않은 채 버텨 왔습니다");
    }

    if (trajectoryId === "hollow-return") {
        if (hasTag("living-return")) push("귀환선은 남았지만 마음의 빚이 지워지지 않았습니다");
        if (scenarioState.frontierTrust <= -10) push("변경의 신뢰가 식으며 귀환의 결이 비어 갑니다");
        if (scenarioState.troopMorale <= -10) push("전열의 지침이 돌아오는 길의 빛을 흐립니다");
    }

    if (trajectoryId === "buried") {
        if (scenarioState.expeditionWear >= 65) push("원정 소모가 임계를 넘고 있습니다");
        if (scenarioState.memorialWeight >= 4) push("추모의 무게가 결말을 아래로 끌어당깁니다");
        if (hasTag("scarred") || hasTag("rear-overrun")) push("상처가 남는 결을 이미 한 번 넘겼습니다");
    }

    if (scenarioState.supplyLineState === "collapsed") {
        push("보급선 붕괴가 모든 결말을 더 가파르게 만듭니다");
    } else if (scenarioState.supplyLineState === "strained") {
        push("보급 경색이 다음 결단을 압박하고 있습니다");
    } else if (scenarioState.supplyLineState === "delayed") {
        push("보급 지연이 천천히 원정의 숨을 좁히고 있습니다");
    }

    if (scenarioState.supplyDebt >= 3) push("그림자 빚이 뒤의 선택을 좁히고 있습니다");
    if (scenarioState.memorialWeight >= 5) push("추모의 밤들이 원정의 무게로 굳고 있습니다");

    if (traces.length === 0) {
        if (scenarioState.troopMorale + scenarioState.frontierTrust >= 10) {
            push("최근 선택들이 버티는 쪽으로 조금씩 모이고 있습니다");
        } else if (scenarioState.troopMorale + scenarioState.frontierTrust <= -10) {
            push("최근 선택들이 원정의 균형을 더 흔들고 있습니다");
        } else {
            push("아직 결말을 굳히기엔 선택의 무게가 팽팽합니다");
        }
    }

    return traces.slice(0, maxItems);
}

function resolveToneColor(tone: "ember" | "moss" | "steel" | "burgundy" | "parchment"): string {
    switch (tone) {
        case "ember": return tokens.color.ember;
        case "moss": return tokens.color.moss;
        case "steel": return tokens.color.steel;
        case "burgundy": return tokens.color.burgundy;
        case "parchment":
        default: return tokens.color.parchment;
    }
}
